import keys from 'constants/keys'
import { setNotificationChatModal, setNotificationPostModal } from 'redux/actions/feed'
import { getTask, patchTaskOverviewModal, setTaskOverviewModal } from 'redux/actions/tasks_v2'
import {
  createServiceNotificationResolver,
  INotificationEventHandler,
} from 'services/notifications/createServiceNotificationResolver'
import { INotification, IResolvedDestination } from 'services/notifications/types'

const getString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed || null
}

const getNumber = (value: unknown): number | null => {
  const parsed = typeof value === 'string' ? Number(value) : value
  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : null
}

const getTaskRef = (notification: INotification) => {
  const objectRef = notification.object_ref ?? notification.data?.object_ref ?? null
  const data = notification.data ?? {}
  // Some backend events wrap the task id as a generic `{ object_type: "task", object_id }` pair
  // instead of a dedicated `tid`/`task_id` field — accept it, but only when object_type says it's
  // actually a task, so this resolver never mistakes another entity's object_id for a task id.
  const objectTypeTid = objectRef?.object_type === 'task' ? objectRef?.object_id : null
  const tid = getString(
    objectRef?.tid ?? objectRef?.task_id ?? objectRef?.oid ?? objectTypeTid ?? data?.tid ?? data?.task_id ?? data?.oid
  )
  const contextId = getString(
    objectRef?.context_id ??
      objectRef?.result_context_id ??
      objectRef?.group_id ??
      data?.context_id ??
      data?.result_context_id ??
      data?.group_id
  )
  const taskContextId = getString(
    data?.task_context_id ?? objectRef?.task_context_id ?? objectRef?.group_id ?? data?.group_id ?? contextId
  )
  const recurrenceTs =
    getString(data?.recurrence_ts ?? objectRef?.recurrence_ts ?? notification.object_ref?.recurrence_ts) ?? null
  const targetSubtaskId = getString(objectRef?.st_id ?? objectRef?.subtask_id ?? data?.st_id ?? data?.subtask_id)
  const targetMediaIndex = getNumber(objectRef?.media_index ?? objectRef?.index ?? data?.media_index ?? data?.index)

  return { tid, contextId, taskContextId, recurrenceTs, targetSubtaskId, targetMediaIndex }
}

const TASK_OVERVIEW_EVENTS = [
  'task_start',
  'task_deadline_passed',
  'task_deadline_day_before',
  'task_deadline_one_hour_before',
  'task_ping',
]

const TASK_RESULT_EVENTS = [
  'task_result_approved',
  'task_result_disapproved',
  'task_result_ai_feedback',
]

const openTask = async (
  notification: INotification,
  dispatch: (action: any) => any,
  options: { openResultTarget?: boolean } = {}
) => {
  const { tid, contextId, taskContextId, recurrenceTs, targetSubtaskId, targetMediaIndex } = getTaskRef(notification)
  if (!tid || !taskContextId) return

  dispatch(setNotificationPostModal(null))
  dispatch(setNotificationChatModal(null))
  dispatch(
    setTaskOverviewModal({
      tid,
      task_context_id: taskContextId,
      result_context_id: contextId ?? taskContextId,
      recurrence_ts: recurrenceTs,
      ...(options.openResultTarget
        ? {
            targetSubtaskId,
            targetMediaIndex,
          }
        : {}),
      isLoading: true,
      error: null,
    })
  )
  const response = await dispatch(
    getTask({
      tid,
      task_context_id: taskContextId,
      result_context_id: contextId ?? taskContextId,
      recurrence_ts: recurrenceTs,
    })
  )

  dispatch(patchTaskOverviewModal({ isLoading: false, error: response.task ? null : 'Unable to load task' }))
}

const buildTaskDashboardPath = (notification: INotification): string => {
  const { tid, recurrenceTs } = getTaskRef(notification)
  if (!tid) return keys.ROUTE_NAMES.NEW_TASK_DASHBOARD

  const params = new URLSearchParams({ tid })
  // Pass recurrence_ts through only when the backend sent it; when missing, the dashboard's own
  // "latest recurrence" fallback (useTaskDashboardData) decides which period to show.
  if (recurrenceTs) params.set('recurrence_ts', recurrenceTs)

  return `${keys.ROUTE_NAMES.NEW_TASK_DASHBOARD}?${params.toString()}`
}

// openTask (onBeforeNavigate) already opens the task directly whenever it has enough data to fetch
// it (tid + task_context_id) — don't ALSO navigate the whole page to the dashboard in that case,
// that's a redundant getInsightsV2 fetch stacked on top of the getTask we just made. Only send the
// user to the dashboard when we genuinely can't fetch the task ourselves (missing fields), which is
// the one case where `handled: true` isn't returned and buildTaskDashboardPath kicks in below.
const resolveTaskDestination = (notification: INotification): IResolvedDestination => {
  if (notification.target?.route) {
    return { path: notification.target.route, replace: true }
  }

  const { tid, taskContextId } = getTaskRef(notification)
  if (tid && taskContextId) return { handled: true }

  return { path: buildTaskDashboardPath(notification), replace: true }
}

const openTaskOverviewEvent: INotificationEventHandler = {
  eventTypes: TASK_OVERVIEW_EVENTS,
  async onBeforeNavigate(notification, { dispatch }) {
    if (!dispatch) return
    await openTask(notification, dispatch)
  },
  async resolve(notification: INotification): Promise<IResolvedDestination> {
    return resolveTaskDestination(notification)
  },
}

const openTaskResultEvent: INotificationEventHandler = {
  eventTypes: TASK_RESULT_EVENTS,
  async onBeforeNavigate(notification, { dispatch }) {
    if (!dispatch) return
    await openTask(notification, dispatch, { openResultTarget: true })
  },
  async resolve(notification: INotification): Promise<IResolvedDestination> {
    return resolveTaskDestination(notification)
  },
}

const fallbackEvent: INotificationEventHandler = {
  eventTypes: [],
  async resolve(): Promise<IResolvedDestination> {
    return { path: keys.ROUTE_NAMES.NEW_TASK_DASHBOARD, replace: true }
  },
}

export const tasksResolver = createServiceNotificationResolver({
  id: 'tasks',
  service: 'tasks',
  events: [openTaskOverviewEvent, openTaskResultEvent],
  fallback: fallbackEvent,
})
